export const DEFAULT_PUBLIC_CUSTOMER_SLUG = 'synaworks';
export const DEFAULT_PUBLIC_WORKSPACE_SLUG = 'main';
export const DEFAULT_PUBLIC_AGENT_SLUG = 'sally';
export const PLATFORM_APEX_HOST = 'synaworks.ai';
export const LEGACY_SALLY_HOST = 'sally.synaworks.ai';
export const SALLY_APEX_AGENT_URL = `https://${PLATFORM_APEX_HOST}/${DEFAULT_PUBLIC_AGENT_SLUG}`;
export const SALLY_PUBLIC_DEMO_URL = `${SALLY_APEX_AGENT_URL}?voice=live&public=demo`;

const PLATFORM_HOSTS = new Set([
  PLATFORM_APEX_HOST,
  `www.${PLATFORM_APEX_HOST}`,
  'localhost',
  '127.0.0.1',
]);

const RESERVED_PUBLIC_SEGMENTS = new Set([
  '',
  'admin',
  'agent',
  'api',
  'c',
  'dashboard',
  'debug',
  'forgot-password',
  'legacy-sally-landing',
  'mode=dev',
  'mode=prod',
  'onboarding',
  'register',
  'reset-password',
  'share',
  'setup',
  'static',
  'workspace',
]);

export interface PublicAgentRouteScope {
  customerSlug: string;
  workspaceSlug: string;
  agentSlug: string;
}

export const sanitizePublicSlug = (value: string | null | undefined, fallback: string): string => {
  const normalized = String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-+|-+$/g, '');

  return normalized || fallback;
};

export const isReservedPublicPathSegment = (segment: string | null | undefined): boolean => {
  return RESERVED_PUBLIC_SEGMENTS.has(String(segment || '').trim().toLowerCase());
};

const firstPathSegment = (pathname: string): string => {
  return (pathname || '').split('/').filter(Boolean)[0] || '';
};

const customerSlugFromHost = (hostname: string): string | null => {
  const host = hostname.trim().toLowerCase();

  if (PLATFORM_HOSTS.has(host) || host === LEGACY_SALLY_HOST) {
    return DEFAULT_PUBLIC_CUSTOMER_SLUG;
  }

  if (!host.endsWith(`.${PLATFORM_APEX_HOST}`)) {
    return null;
  }

  const subdomain = host.slice(0, -(`.${PLATFORM_APEX_HOST}`).length);
  const firstLabel = subdomain.split('.').filter(Boolean)[0] || '';
  if (!firstLabel || firstLabel === 'www' || firstLabel === 'sally') {
    return DEFAULT_PUBLIC_CUSTOMER_SLUG;
  }

  return sanitizePublicSlug(firstLabel, DEFAULT_PUBLIC_CUSTOMER_SLUG);
};

export const shouldRedirectLegacySallyRoot = (hostname: string, pathname: string): boolean => {
  return hostname.trim().toLowerCase() === LEGACY_SALLY_HOST && (pathname || '/') === '/';
};

export const resolvePublicAgentRouteScope = (
  hostname: string,
  pathname: string,
  explicitAgentSlug?: string | null,
): PublicAgentRouteScope | null => {
  const hostCustomerSlug = customerSlugFromHost(hostname);
  if (!hostCustomerSlug) {
    return null;
  }

  const rawAgentSlug = explicitAgentSlug || firstPathSegment(pathname) || DEFAULT_PUBLIC_AGENT_SLUG;
  if (isReservedPublicPathSegment(rawAgentSlug)) {
    return null;
  }

  return {
    customerSlug: hostCustomerSlug,
    workspaceSlug: DEFAULT_PUBLIC_WORKSPACE_SLUG,
    agentSlug: sanitizePublicSlug(rawAgentSlug, DEFAULT_PUBLIC_AGENT_SLUG),
  };
};
